import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Plus, Trash2, Bed, LogIn, LogOut, X, PawPrint, Package as PackageIcon } from "lucide-react";
import { toast } from "sonner";
import CustomerSearchPicker from "../components/CustomerSearchPicker";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);
const addDays = (d, n) => { const dt = new Date(d); dt.setDate(dt.getDate() + n); return dt.toISOString().slice(0, 10); };

export default function Boarding() {
  return (
    <div className="space-y-5" data-testid="boarding-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Pet Hotel</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Boarding</h1>
        <p className="text-sm text-muted-foreground mt-1">Reservations, rooms / kennels and tier pricing for pet stays.</p>
      </div>

      <Tabs defaultValue="reservations">
        <TabsList>
          <TabsTrigger value="reservations" data-testid="tab-bd-reservations">Reservations</TabsTrigger>
          <TabsTrigger value="rooms" data-testid="tab-bd-rooms">Rooms</TabsTrigger>
          <TabsTrigger value="rates" data-testid="tab-bd-rates">Rates</TabsTrigger>
        </TabsList>
        <TabsContent value="reservations" className="mt-4"><Reservations /></TabsContent>
        <TabsContent value="rooms" className="mt-4"><Rooms /></TabsContent>
        <TabsContent value="rates" className="mt-4"><Rates /></TabsContent>
      </Tabs>
    </div>
  );
}

function Rooms() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [f, setF] = useState({ name: "", capacity: 1, size_class: "any", notes: "", is_active: true });

  const load = () => api.get("/boarding/rooms").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEdit(null); setF({ name: "", capacity: 1, size_class: "any", notes: "", is_active: true }); setOpen(true); };
  const openEdit = (r) => { setEdit(r); setF({ name: r.name, capacity: r.capacity, size_class: r.size_class || "any", notes: r.notes || "", is_active: r.is_active }); setOpen(true); };

  const save = async () => {
    if (!f.name?.trim()) { toast.error("Room name required"); return; }
    try {
      if (edit) await api.patch(`/boarding/rooms/${edit.id}`, f);
      else await api.post("/boarding/rooms", f);
      toast.success("Room saved");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete room ${r.name}?`)) return;
    try { await api.delete(`/boarding/rooms/${r.id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-3">
      <Card className="p-4 flex items-center gap-3 flex-wrap">
        <Bed className="w-5 h-5 text-primary" />
        <div className="text-sm text-muted-foreground">{rows.length} room{rows.length === 1 ? "" : "s"}</div>
        <Button className="ml-auto" onClick={openCreate} data-testid="add-room-btn"><Plus className="w-4 h-4 mr-1" />New room</Button>
      </Card>
      <Card className="p-4">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Size class</TableHead>
            <TableHead>Capacity</TableHead>
            <TableHead>Occupied</TableHead>
            <TableHead>Status</TableHead>
            <TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.id} data-testid={`room-${r.id}`}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell><Badge variant="outline" className="text-xs">{r.size_class || "any"}</Badge></TableCell>
                <TableCell>{r.capacity}</TableCell>
                <TableCell>
                  <span className={r.available === 0 ? "text-rose-700" : "text-emerald-700"}>
                    {r.current_occupants} / {r.capacity}
                  </span>
                </TableCell>
                <TableCell>{r.is_active ? <Badge variant="secondary">active</Badge> : <Badge variant="destructive">inactive</Badge>}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(r)}>Edit</Button>
                  <Button variant="ghost" size="sm" onClick={() => del(r)} className="text-destructive"><Trash2 className="w-4 h-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">No rooms yet. Add your first kennel to start taking reservations.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="room-dialog">
          <DialogHeader><DialogTitle>{edit ? "Edit room" : "New room"}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Name *</Label>
            <Input value={f.name} onChange={e => setF(p => ({ ...p, name: e.target.value }))} placeholder='e.g. "Kennel A1" or "Suite 2"' data-testid="room-name" />
            <Label className="text-xs">Size class</Label>
            <Select value={f.size_class} onValueChange={v => setF(p => ({ ...p, size_class: v }))}>
              <SelectTrigger data-testid="room-size"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any</SelectItem>
                <SelectItem value="cat">Cat</SelectItem>
                <SelectItem value="mini">Dog mini</SelectItem>
                <SelectItem value="med">Dog medium</SelectItem>
                <SelectItem value="giant">Dog giant</SelectItem>
              </SelectContent>
            </Select>
            <Label className="text-xs">Capacity (pets in this room)</Label>
            <Input type="number" min="1" max="10" value={f.capacity} onChange={e => setF(p => ({ ...p, capacity: Math.max(1, parseInt(e.target.value) || 1) }))} data-testid="room-capacity" />
            <Label className="text-xs">Notes</Label>
            <Input value={f.notes} onChange={e => setF(p => ({ ...p, notes: e.target.value }))} placeholder="AC, ground floor, near garden, etc." />
            {edit && (
              <label className="flex items-center gap-2 text-sm pt-1">
                <input type="checkbox" checked={f.is_active} onChange={e => setF(p => ({ ...p, is_active: e.target.checked }))} />
                Active (uncheck to retire room)
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="room-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Rates() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/boarding/rates").then(r => setS(r.data)); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/boarding/rates", {
        cat_per_day: Number(s.cat_per_day) || 0,
        dog_mini_per_day: Number(s.dog_mini_per_day) || 0,
        dog_giant_per_day: Number(s.dog_giant_per_day) || 0,
        gst_percent: Number(s.gst_percent) || 0,
      });
      toast.success("Rates saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!s) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <Card className="p-5 max-w-xl space-y-3" data-testid="rates-card">
      <div className="text-sm text-muted-foreground">Per-day rates used by default when creating a new reservation. You can still override the rate on individual bookings.</div>
      <div className="grid grid-cols-3 gap-3">
        <RateInput label="Cat" value={s.cat_per_day} onChange={v => setS(p => ({ ...p, cat_per_day: v }))} tid="rate-cat" />
        <RateInput label="Dog mini" value={s.dog_mini_per_day} onChange={v => setS(p => ({ ...p, dog_mini_per_day: v }))} tid="rate-dog-mini" />
        <RateInput label="Dog giant" value={s.dog_giant_per_day} onChange={v => setS(p => ({ ...p, dog_giant_per_day: v }))} tid="rate-dog-giant" />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <RateInput label="GST %" value={s.gst_percent} onChange={v => setS(p => ({ ...p, gst_percent: v }))} tid="rate-gst" />
      </div>
      <Button onClick={save} disabled={busy} data-testid="rates-save">{busy ? "Saving…" : "Save rates"}</Button>
    </Card>
  );
}
const RateInput = ({ label, value, onChange, tid }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <Input type="number" min="0" value={value || 0} onChange={e => onChange(parseFloat(e.target.value) || 0)} data-testid={tid} />
  </div>
);

function Reservations() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("active"); // active = reserved or checked_in
  const [open, setOpen] = useState(false);
  const [checkoutFor, setCheckoutFor] = useState(null);

  const load = () => {
    const params = {};
    if (filter === "active") {
      // active means reserved or checked_in — fetch both
      Promise.all([
        api.get("/boarding/reservations", { params: { status: "reserved" } }),
        api.get("/boarding/reservations", { params: { status: "checked_in" } }),
      ]).then(([a, b]) => setRows([...a.data, ...b.data].sort((x, y) => (y.check_in_date || "").localeCompare(x.check_in_date || ""))));
    } else if (filter === "all") {
      api.get("/boarding/reservations").then(r => setRows(r.data));
    } else {
      params.status = filter;
      api.get("/boarding/reservations", { params }).then(r => setRows(r.data));
    }
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const checkin = async (r) => {
    try { await api.post(`/boarding/reservations/${r.id}/checkin`); toast.success("Checked in"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const cancel = async (r) => {
    if (!window.confirm(`Cancel reservation ${r.reservation_no}?`)) return;
    try { await api.delete(`/boarding/reservations/${r.id}`); toast.success("Cancelled"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-3">
      <Card className="p-4 flex items-center gap-3 flex-wrap">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-44" data-testid="bd-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active (reserved + checked-in)</SelectItem>
            <SelectItem value="reserved">Upcoming</SelectItem>
            <SelectItem value="checked_in">Currently staying</SelectItem>
            <SelectItem value="checked_out">Checked out</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-sm text-muted-foreground">{rows.length} reservation{rows.length === 1 ? "" : "s"}</div>
        <Button className="ml-auto" onClick={() => setOpen(true)} data-testid="new-reservation-btn"><Plus className="w-4 h-4 mr-1" />New reservation</Button>
      </Card>

      <Card className="p-4">
        <Table>
          <TableHeader><TableRow>
            <TableHead>BR #</TableHead>
            <TableHead>Customer & Pet</TableHead>
            <TableHead>Room</TableHead>
            <TableHead>Stay</TableHead>
            <TableHead>Tier</TableHead>
            <TableHead className="text-right">Estimate</TableHead>
            <TableHead>Status</TableHead>
            <TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.id} data-testid={`reservation-${r.id}`}>
                <TableCell className="font-mono text-xs">{r.reservation_no}</TableCell>
                <TableCell>
                  <div className="font-medium text-sm">{r.customer_name || "—"}</div>
                  <div className="text-xs text-muted-foreground"><PawPrint className="inline w-3 h-3 mr-1" />{r.pet_name} ({r.pet_species})</div>
                </TableCell>
                <TableCell>{r.room_name || "—"}</TableCell>
                <TableCell className="text-xs">{r.check_in_date} → {r.actual_check_out_date || r.expected_check_out_date}</TableCell>
                <TableCell><Badge variant="outline" className="text-xs">{r.tier.replace("_", " ")}</Badge></TableCell>
                <TableCell className="text-right font-mono">{inr(r.final_total ?? r.estimated_total)}</TableCell>
                <TableCell><StatusBadge s={r.status} /></TableCell>
                <TableCell className="text-right">
                  {r.status === "reserved" && (
                    <>
                      <Button variant="outline" size="sm" onClick={() => checkin(r)} data-testid={`checkin-${r.id}`}><LogIn className="w-3 h-3 mr-1" />Check in</Button>
                      <Button variant="ghost" size="sm" onClick={() => cancel(r)} className="text-destructive"><X className="w-4 h-4" /></Button>
                    </>
                  )}
                  {r.status === "checked_in" && (
                    <Button variant="outline" size="sm" onClick={() => setCheckoutFor(r)} data-testid={`checkout-${r.id}`}><LogOut className="w-3 h-3 mr-1" />Check out</Button>
                  )}
                  {(r.belongings || []).length > 0 && (
                    <Badge variant="secondary" className="ml-1"><PackageIcon className="w-3 h-3 mr-1" />{r.belongings.length}</Badge>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-6">No reservations.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <NewReservationDialog open={open} onOpenChange={setOpen} onSaved={load} />
      <CheckoutDialog r={checkoutFor} onClose={() => { setCheckoutFor(null); load(); }} />
    </div>
  );
}

const StatusBadge = ({ s }) => {
  const colors = {
    reserved: "bg-blue-100 text-blue-800",
    checked_in: "bg-purple-100 text-purple-800",
    checked_out: "bg-emerald-100 text-emerald-800",
    cancelled: "bg-rose-100 text-rose-800",
  };
  return <Badge className={`${colors[s] || ""} text-xs`}>{s.replace("_", " ")}</Badge>;
};

function NewReservationDialog({ open, onOpenChange, onSaved }) {
  const [customer, setCustomer] = useState(null);
  const [pets, setPets] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [rates, setRates] = useState(null);
  const [f, setF] = useState({
    pet_id: "", room_id: "",
    check_in_date: today(), check_in_time: "10:00",
    expected_check_out_date: addDays(today(), 1), expected_check_out_time: "10:00",
    tier: "dog_mini", daily_rate: "",
    belongings: [], notes: "",
  });
  const [newCustOpen, setNewCustOpen] = useState(false);
  const [newPetOpen, setNewPetOpen] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/boarding/rooms").then(r => setRooms(r.data.filter(x => x.is_active && x.available > 0)));
      api.get("/boarding/rates").then(r => setRates(r.data));
      setCustomer(null);
      setF({ pet_id: "", room_id: "", check_in_date: today(), check_in_time: "10:00", expected_check_out_date: addDays(today(), 1), expected_check_out_time: "10:00", tier: "dog_mini", daily_rate: "", belongings: [], notes: "" });
    }
  }, [open]);

  useEffect(() => {
    if (customer?.id) api.get("/pets", { params: { customer_id: customer.id } }).then(r => setPets(r.data));
    else setPets([]);
  }, [customer]);

  const days = Math.max(1, Math.ceil((new Date(f.expected_check_out_date) - new Date(f.check_in_date)) / 86400000));
  const tierDefault = rates ? ({ cat: rates.cat_per_day, dog_mini: rates.dog_mini_per_day, dog_giant: rates.dog_giant_per_day }[f.tier] || 0) : 0;
  const dailyRate = f.daily_rate !== "" ? Number(f.daily_rate) : tierDefault;
  const estimate = (dailyRate * days).toFixed(2);

  const addBelonging = () => setF(p => ({ ...p, belongings: [...p.belongings, { item: "", qty: 1, condition: "" }] }));
  const removeBelonging = (i) => setF(p => ({ ...p, belongings: p.belongings.filter((_, idx) => idx !== i) }));
  const updateBelonging = (i, patch) => setF(p => ({ ...p, belongings: p.belongings.map((b, idx) => idx === i ? { ...b, ...patch } : b) }));

  const save = async () => {
    if (!customer?.id || !f.pet_id || !f.room_id) { toast.error("Customer, pet and room are required"); return; }
    try {
      await api.post("/boarding/reservations", {
        ...f,
        customer_id: customer.id,
        daily_rate: f.daily_rate !== "" ? Number(f.daily_rate) : null,
        belongings: f.belongings.filter(b => b.item?.trim()),
      });
      toast.success("Reservation created");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-reservation-dialog">
          <DialogHeader><DialogTitle>New boarding reservation</DialogTitle></DialogHeader>

          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label className="text-xs">Customer *</Label>
              <CustomerSearchPicker
                value={customer}
                onChange={(c) => { setCustomer(c); setF(p => ({ ...p, pet_id: "" })); }}
                onClear={() => { setCustomer(null); setF(p => ({ ...p, pet_id: "" })); }}
                onNewClick={() => setNewCustOpen(true)}
                placeholder="Search by name or mobile number…"
                testIdPrefix="bd-cust"
                autoFocus
              />
            </div>
            <div>
              <Label className="text-xs">Pet *</Label>
              <div className="flex gap-1">
                <Select value={f.pet_id} onValueChange={v => setF(p => ({ ...p, pet_id: v }))} disabled={!customer?.id}>
                  <SelectTrigger data-testid="bd-pet"><SelectValue placeholder={customer?.id ? "Select pet…" : "Pick customer first"} /></SelectTrigger>
                  <SelectContent>{pets.map(p => <SelectItem key={p.id} value={p.id}>{p.name} ({p.species})</SelectItem>)}</SelectContent>
                </Select>
                <Button variant="outline" size="icon" type="button" onClick={() => setNewPetOpen(true)} disabled={!customer?.id} data-testid="bd-new-pet"><Plus className="w-4 h-4" /></Button>
              </div>
            </div>
            <div>
              <Label className="text-xs">Room *</Label>
              <Select value={f.room_id} onValueChange={v => setF(p => ({ ...p, room_id: v }))}>
                <SelectTrigger data-testid="bd-room"><SelectValue placeholder="Select room…" /></SelectTrigger>
                <SelectContent>{rooms.map(r => <SelectItem key={r.id} value={r.id}>{r.name} ({r.size_class || "any"}) · {r.available}/{r.capacity} free</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Tier *</Label>
              <Select value={f.tier} onValueChange={v => setF(p => ({ ...p, tier: v }))}>
                <SelectTrigger data-testid="bd-tier"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cat">Cat · ₹{rates?.cat_per_day}/day</SelectItem>
                  <SelectItem value="dog_mini">Dog mini · ₹{rates?.dog_mini_per_day}/day</SelectItem>
                  <SelectItem value="dog_giant">Dog giant · ₹{rates?.dog_giant_per_day}/day</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Check-in date *</Label>
              <Input type="date" value={f.check_in_date} onChange={e => setF(p => ({ ...p, check_in_date: e.target.value }))} data-testid="bd-checkin-date" />
            </div>
            <div>
              <Label className="text-xs">Check-in time</Label>
              <Input type="time" value={f.check_in_time} onChange={e => setF(p => ({ ...p, check_in_time: e.target.value }))} data-testid="bd-checkin-time" />
            </div>
            <div>
              <Label className="text-xs">Expected check-out date *</Label>
              <Input type="date" value={f.expected_check_out_date} onChange={e => setF(p => ({ ...p, expected_check_out_date: e.target.value }))} data-testid="bd-checkout-date" />
            </div>
            <div>
              <Label className="text-xs">Expected check-out time</Label>
              <Input type="time" value={f.expected_check_out_time} onChange={e => setF(p => ({ ...p, expected_check_out_time: e.target.value }))} data-testid="bd-checkout-time" />
            </div>
            <div>
              <Label className="text-xs">Daily rate (override)</Label>
              <Input type="number" min="0" value={f.daily_rate} onChange={e => setF(p => ({ ...p, daily_rate: e.target.value }))} placeholder={tierDefault.toString()} data-testid="bd-rate" />
            </div>
            <div>
              <Label className="text-xs">Estimate</Label>
              <div className="font-heading text-2xl font-bold mt-1">{inr(estimate)} <span className="text-xs text-muted-foreground font-normal">· {days} day{days === 1 ? "" : "s"}</span></div>
            </div>
          </div>

          <div className="space-y-2 mt-2 border-t border-border pt-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Belongings checked in</Label>
              <Button variant="ghost" size="sm" onClick={addBelonging} data-testid="add-belonging"><Plus className="w-3 h-3 mr-1" />Add item</Button>
            </div>
            {f.belongings.length === 0 && <div className="text-xs text-muted-foreground">No belongings recorded.</div>}
            {f.belongings.map((b, i) => (
              <div key={i} className="grid grid-cols-[1fr_70px_1fr_30px] gap-2 items-center">
                <Input placeholder="Item (collar, food bag…)" value={b.item} onChange={e => updateBelonging(i, { item: e.target.value })} data-testid={`belong-item-${i}`} />
                <Input type="number" min="1" value={b.qty} onChange={e => updateBelonging(i, { qty: parseInt(e.target.value) || 1 })} />
                <Input placeholder="Condition / notes" value={b.condition} onChange={e => updateBelonging(i, { condition: e.target.value })} />
                <Button variant="ghost" size="icon" onClick={() => removeBelonging(i)}><Trash2 className="w-3 h-3 text-destructive" /></Button>
              </div>
            ))}
          </div>

          <div className="mt-2">
            <Label className="text-xs">Notes</Label>
            <Input value={f.notes} onChange={e => setF(p => ({ ...p, notes: e.target.value }))} placeholder="Feeding schedule, medical conditions, special handling…" />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={save} data-testid="bd-save">Create reservation</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <QuickNewCustomerDialog
        open={newCustOpen} onOpenChange={setNewCustOpen}
        onCreated={(c) => { setCustomer(c); setF(p => ({ ...p, pet_id: "" })); setNewCustOpen(false); }}
      />
      <QuickNewPetDialog
        open={newPetOpen} onOpenChange={setNewPetOpen} customerId={customer?.id}
        onCreated={(p) => { setPets(prev => [p, ...prev]); setF(prev => ({ ...prev, pet_id: p.id })); setNewPetOpen(false); }}
      />
    </>
  );
}

function CheckoutDialog({ r, onClose }) {
  const [extras, setExtras] = useState(0);
  const [note, setNote] = useState("");
  const [actual, setActual] = useState(today());
  const [actualTime, setActualTime] = useState("10:00");

  useEffect(() => { if (r) { setExtras(0); setNote(""); setActual(today()); setActualTime("10:00"); } }, [r]);
  if (!r) return null;

  const days = Math.max(1, Math.ceil((new Date(actual) - new Date(r.check_in_date)) / 86400000));
  const base = (r.daily_rate * days);
  const total = base + Number(extras || 0);

  const submit = async () => {
    try {
      await api.post(`/boarding/reservations/${r.id}/checkout`, { actual_check_out_date: actual, actual_check_out_time: actualTime, extras_amount: Number(extras) || 0, extras_note: note });
      toast.success(`Checked out — total ${inr(total)}. Add to next POS bill.`);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={!!r} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="checkout-dialog">
        <DialogHeader><DialogTitle>Check out {r.reservation_no}</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <div className="text-sm">{r.customer_name} · {r.pet_name} · {r.room_name}</div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Actual check-out date</Label>
              <Input type="date" value={actual} onChange={e => setActual(e.target.value)} data-testid="checkout-date" />
            </div>
            <div>
              <Label className="text-xs">Time</Label>
              <Input type="time" value={actualTime} onChange={e => setActualTime(e.target.value)} data-testid="checkout-time" />
            </div>
          </div>
          <div className="text-xs text-muted-foreground">Stay: {days} day{days === 1 ? "" : "s"} × ₹{r.daily_rate} = {inr(base)}</div>
          <Label className="text-xs">Extras (₹)</Label>
          <Input type="number" min="0" value={extras} onChange={e => setExtras(e.target.value)} placeholder="Late fee, food upgrade…" data-testid="checkout-extras" />
          <Label className="text-xs">Extras note</Label>
          <Input value={note} onChange={e => setNote(e.target.value)} />
          <div className="border-t border-border pt-2 mt-2 text-right">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Grand total</div>
            <div className="font-heading text-2xl font-bold">{inr(total)}</div>
          </div>
          {(r.belongings || []).length > 0 && (
            <div className="border-t border-border pt-2 mt-2">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Return checklist</div>
              {r.belongings.map((b, i) => (
                <div key={i} className="text-xs flex justify-between">
                  <span>☐ {b.item} ({b.qty})</span>
                  <span className="text-muted-foreground">{b.condition || ""}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} data-testid="confirm-checkout"><LogOut className="w-4 h-4 mr-1" />Check out & bill</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function QuickNewCustomerDialog({ open, onOpenChange, onCreated }) {
  const [f, setF] = useState({ name: "", mobile: "", email: "" });
  useEffect(() => { if (open) setF({ name: "", mobile: "", email: "" }); }, [open]);
  const submit = async () => {
    if (!f.name?.trim() || !f.mobile?.trim()) { toast.error("Name and mobile required"); return; }
    try {
      const { data } = await api.post("/customers", f);
      toast.success(`Customer "${data.name}" created`);
      onCreated(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="quick-customer-dialog">
        <DialogHeader><DialogTitle>Quick add customer</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <Label className="text-xs">Name *</Label>
          <Input value={f.name} onChange={e => setF(p => ({ ...p, name: e.target.value }))} autoFocus data-testid="qc-name" />
          <Label className="text-xs">Mobile *</Label>
          <Input value={f.mobile} onChange={e => setF(p => ({ ...p, mobile: e.target.value }))} data-testid="qc-mobile" />
          <Label className="text-xs">Email</Label>
          <Input value={f.email} onChange={e => setF(p => ({ ...p, email: e.target.value }))} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} data-testid="qc-save">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function QuickNewPetDialog({ open, onOpenChange, customerId, onCreated }) {
  const [f, setF] = useState({ name: "", species: "dog", breed: "" });
  useEffect(() => { if (open) setF({ name: "", species: "dog", breed: "" }); }, [open]);
  const submit = async () => {
    if (!f.name?.trim()) { toast.error("Pet name required"); return; }
    if (!customerId) { toast.error("Pick a customer first"); return; }
    try {
      const { data } = await api.post("/pets", { ...f, customer_id: customerId });
      toast.success(`Pet "${data.name}" created`);
      onCreated(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="quick-pet-dialog">
        <DialogHeader><DialogTitle>Quick add pet</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <Label className="text-xs">Name *</Label>
          <Input value={f.name} onChange={e => setF(p => ({ ...p, name: e.target.value }))} autoFocus data-testid="qp-name" />
          <Label className="text-xs">Species</Label>
          <Select value={f.species} onValueChange={v => setF(p => ({ ...p, species: v }))}>
            <SelectTrigger data-testid="qp-species"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="dog">Dog</SelectItem>
              <SelectItem value="cat">Cat</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
          <Label className="text-xs">Breed</Label>
          <Input value={f.breed} onChange={e => setF(p => ({ ...p, breed: e.target.value }))} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} data-testid="qp-save">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
